import React, { useRef, useState } from 'react';
import { Settings, Loader2 } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { TokenPicker, TokenDef } from '../../ui/TokenPicker';
import { Bold, Italic, Underline } from 'lucide-react';
import { wrapNotificationSelection } from '../../PrestationPlanning/PrestationNotificationsPage';

/** Barre de mise en forme des e-mails convives. Réutilise wrapNotificationSelection et les marqueurs déjà
 *  interprétés par templateBodyToHtml côté serveur (** gras, * italique, __ souligné, [[taille=Npx]]) —
 *  aucun nouveau format à supporter à l'envoi, et le rendu est exactement celui des autres e-mails de
 *  l'application. Demande d'Emeni (2026-08-23). */
const FONT_SIZES = [
    { value: '12', label: 'Petit' },
    { value: '16', label: 'Normal' },
    { value: '20', label: 'Grand' },
    { value: '28', label: 'Très grand' },
];

function FormatBar({ textareaRef, value, onChange }: {
    textareaRef: React.RefObject<HTMLTextAreaElement | null>;
    value: string;
    onChange: (v: string) => void;
}) {
    const btn = 'p-1.5 rounded-md border border-border bg-surface-soft text-content-muted hover:text-content';
    return (
        <div className="flex items-center gap-1">
            <button type="button" title="Gras" className={btn}
                onClick={() => wrapNotificationSelection(textareaRef.current, value, onChange, '**')}><Bold size={12} /></button>
            <button type="button" title="Italique" className={btn}
                onClick={() => wrapNotificationSelection(textareaRef.current, value, onChange, '*')}><Italic size={12} /></button>
            <button type="button" title="Souligné" className={btn}
                onClick={() => wrapNotificationSelection(textareaRef.current, value, onChange, '__')}><Underline size={12} /></button>
            <select defaultValue="" title="Taille du texte sélectionné"
                onChange={(e) => { if (e.target.value) { wrapNotificationSelection(textareaRef.current, value, onChange, `[[taille=${e.target.value}px]]`, '[[/taille]]'); e.target.value = ''; } }}
                className="p-1.5 rounded-md border border-border bg-surface-soft text-content-muted hover:text-content text-[11px] outline-none">
                <option value="" disabled>Taille</option>
                {FONT_SIZES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <span className="text-[10px] text-content-muted ml-1">Sélectionne du texte puis clique.</span>
        </div>
    );
}
import { moduleApi } from '../../../lib/api';

const TOKENS: TokenDef[] = [
    { token: '{{prenom}}', desc: 'Prénom du convive' },
    { token: '{{evenement}}', desc: "Nom de l'événement" },
    { token: '{{date}}', desc: "Date de l'événement" },
    { token: '{{lien}}', desc: 'Lien vers le choix du repas' },
];
// Pas de {{lien}} pour la clôture — il n'y a plus rien à choisir en ligne à ce stade.
const CLOSURE_TOKENS: TokenDef[] = TOKENS.filter((t) => t.token !== '{{lien}}');

// {{categorie}} n'a de sens que pour cet email (la catégorie du plat de secours proposé) — ajouté à ce
// seul jeu de tokens plutôt qu'au socle commun.
const ALT_DISH_TOKENS: TokenDef[] = [...TOKENS, { token: '{{categorie}}', desc: 'Catégorie concernée (ex: Dessert)' }];

// {{date_limite}} : dernier jour où le convive peut répondre — propre à l'avertissement avant clôture.
const PREWARNING_TOKENS: TokenDef[] = [...TOKENS, { token: '{{date_limite}}', desc: 'Dernier jour pour répondre' }];

interface EmailSettings { invitation_subject: string; invitation_body: string; reminder_subject: string; reminder_body: string; closure_subject: string; closure_body: string; alt_dish_subject: string; alt_dish_body: string; prewarning_subject: string; prewarning_body: string; }

/** Réglages des emails envoyés aux convives (invitation + relance), au niveau du client — réutilisés pour
 *  tous ses événements, comme demandé par Emeni ("bouton paramètre pour personnaliser les email que les
 *  convive reçoivent"). Même gabarit que SendCustomMessageModal (Modal + TokenPicker), avec deux sections
 *  (invitation / relance) au lieu d'une seule. */
export function ConviveEmailSettingsModal({ onClose }: { onClose: () => void }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    const [settings, setSettings] = useState<EmailSettings>({ invitation_subject: '', invitation_body: '', reminder_subject: '', reminder_body: '', closure_subject: '', closure_body: '', alt_dish_subject: '', alt_dish_body: '', prewarning_subject: '', prewarning_body: '' });
    const invitationBodyRef = useRef<HTMLTextAreaElement>(null);
    const reminderBodyRef = useRef<HTMLTextAreaElement>(null);
    const closureBodyRef = useRef<HTMLTextAreaElement>(null);
    const altDishBodyRef = useRef<HTMLTextAreaElement>(null);
    const prewarningBodyRef = useRef<HTMLTextAreaElement>(null);

    React.useEffect(() => {
        moduleApi.getConviveEmailSettings()
            .then((s: any) => setSettings({
                invitation_subject: s.invitation_subject || '',
                invitation_body: s.invitation_body || '',
                reminder_subject: s.reminder_subject || '',
                reminder_body: s.reminder_body || '',
                closure_subject: s.closure_subject || '',
                closure_body: s.closure_body || '',
                alt_dish_subject: s.alt_dish_subject || '',
                alt_dish_body: s.alt_dish_body || '',
                prewarning_subject: s.prewarning_subject || '',
                prewarning_body: s.prewarning_body || '',
            }))
            .catch((e: any) => setError(e?.message || 'Erreur lors du chargement.'))
            .finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        setSaving(true);
        setError('');
        setSaved(false);
        try {
            await moduleApi.updateConviveEmailSettings(settings);
            setSaved(true);
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'enregistrement.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open onClose={onClose} title="Emails des convives" icon={<Settings size={16} />} maxWidth="max-w-lg"
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Fermer</Button>
                    <Button variant="primary" onClick={handleSave} disabled={saving || loading}>{saving ? 'Enregistrement...' : 'Enregistrer'}</Button>
                </>
            }>
            {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="animate-spin text-content-muted" size={22} /></div>
            ) : (
                <div className="space-y-5">
                    <div className="space-y-2.5">
                        <h3 className="text-xs font-black text-content uppercase tracking-widest">Invitation</h3>
                        <div>
                            <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Sujet</label>
                            <input type="text" value={settings.invitation_subject} onChange={(e) => setSettings((s) => ({ ...s, invitation_subject: e.target.value }))}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors" />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Message</label>
                            <FormatBar textareaRef={invitationBodyRef} value={settings.invitation_body}
                                onChange={(v) => setSettings((s) => ({ ...s, invitation_body: v }))} />
                            <textarea ref={invitationBodyRef} value={settings.invitation_body} onChange={(e) => setSettings((s) => ({ ...s, invitation_body: e.target.value }))} rows={5}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors resize-none" />
                        </div>
                        <TokenPicker tokens={TOKENS} textareaRef={invitationBodyRef} value={settings.invitation_body} onChange={(v) => setSettings((s) => ({ ...s, invitation_body: v }))} />
                    </div>

                    <div className="space-y-2.5 pt-3 border-t border-border">
                        <h3 className="text-xs font-black text-content uppercase tracking-widest">Relance</h3>
                        <div>
                            <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Sujet</label>
                            <input type="text" value={settings.reminder_subject} onChange={(e) => setSettings((s) => ({ ...s, reminder_subject: e.target.value }))}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors" />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Message</label>
                            <FormatBar textareaRef={reminderBodyRef} value={settings.reminder_body}
                                onChange={(v) => setSettings((s) => ({ ...s, reminder_body: v }))} />
                            <textarea ref={reminderBodyRef} value={settings.reminder_body} onChange={(e) => setSettings((s) => ({ ...s, reminder_body: e.target.value }))} rows={5}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors resize-none" />
                        </div>
                        <TokenPicker tokens={TOKENS} textareaRef={reminderBodyRef} value={settings.reminder_body} onChange={(v) => setSettings((s) => ({ ...s, reminder_body: v }))} />
                    </div>

                    {/* Avertissement AVANT la fermeture, à ne pas confondre avec la clôture juste en dessous :
                        celui-ci laisse encore au convive le temps de répondre, l'autre constate que c'est
                        trop tard. Idée d'Emeni (2026-08-24). */}
                    <div className="space-y-2.5 pt-3 border-t border-border">
                        <h3 className="text-xs font-black text-content uppercase tracking-widest">Avertissement avant clôture</h3>
                        <p className="text-[11px] text-content-muted">Envoyé automatiquement, le matin de la date programmée sur l'événement, aux convives n'ayant pas encore répondu.</p>
                        <div>
                            <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Sujet</label>
                            <input type="text" value={settings.prewarning_subject} onChange={(e) => setSettings((s) => ({ ...s, prewarning_subject: e.target.value }))}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors" />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Message</label>
                            <FormatBar textareaRef={prewarningBodyRef} value={settings.prewarning_body}
                                onChange={(v) => setSettings((s) => ({ ...s, prewarning_body: v }))} />
                            <textarea ref={prewarningBodyRef} value={settings.prewarning_body} onChange={(e) => setSettings((s) => ({ ...s, prewarning_body: e.target.value }))} rows={5}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors resize-none" />
                        </div>
                        <TokenPicker tokens={PREWARNING_TOKENS} textareaRef={prewarningBodyRef} value={settings.prewarning_body} onChange={(v) => setSettings((s) => ({ ...s, prewarning_body: v }))} />
                    </div>

                    <div className="space-y-2.5 pt-3 border-t border-border">
                        <h3 className="text-xs font-black text-content uppercase tracking-widest">Clôture (non-répondants)</h3>
                        <p className="text-[11px] text-content-muted">Envoyé au clic sur "Clôturer" à tous les convives n'ayant jamais répondu.</p>
                        <div>
                            <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Sujet</label>
                            <input type="text" value={settings.closure_subject} onChange={(e) => setSettings((s) => ({ ...s, closure_subject: e.target.value }))}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors" />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Message</label>
                            <FormatBar textareaRef={closureBodyRef} value={settings.closure_body}
                                onChange={(v) => setSettings((s) => ({ ...s, closure_body: v }))} />
                            <textarea ref={closureBodyRef} value={settings.closure_body} onChange={(e) => setSettings((s) => ({ ...s, closure_body: e.target.value }))} rows={5}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors resize-none" />
                        </div>
                        <TokenPicker tokens={CLOSURE_TOKENS} textareaRef={closureBodyRef} value={settings.closure_body} onChange={(v) => setSettings((s) => ({ ...s, closure_body: v }))} />
                    </div>

                    <div className="space-y-2.5 pt-3 border-t border-border">
                        <h3 className="text-xs font-black text-content uppercase tracking-widest">Plat alternatif</h3>
                        <p className="text-[11px] text-content-muted">Envoyé quand un plat de secours est proposé à un convive n'ayant pu rien choisir dans une catégorie.</p>
                        <div>
                            <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Sujet</label>
                            <input type="text" value={settings.alt_dish_subject} onChange={(e) => setSettings((s) => ({ ...s, alt_dish_subject: e.target.value }))}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors" />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Message</label>
                            <FormatBar textareaRef={altDishBodyRef} value={settings.alt_dish_body}
                                onChange={(v) => setSettings((s) => ({ ...s, alt_dish_body: v }))} />
                            <textarea ref={altDishBodyRef} value={settings.alt_dish_body} onChange={(e) => setSettings((s) => ({ ...s, alt_dish_body: e.target.value }))} rows={5}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors resize-none" />
                        </div>
                        <TokenPicker tokens={ALT_DISH_TOKENS} textareaRef={altDishBodyRef} value={settings.alt_dish_body} onChange={(v) => setSettings((s) => ({ ...s, alt_dish_body: v }))} />
                    </div>

                    {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
                    {saved && !error && <p className="text-xs text-brand-600 dark:text-brand-400 font-semibold">Réglages enregistrés.</p>}
                </div>
            )}
        </Modal>
    );
}
